import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { commitMutation, graphql } from 'react-relay';
import { Analytics, GlobalNotifications, Intent } from '../../../Modules';
import SignatureBlocks from '../../Sections/SignatureBlocks/SignatureBlocks';
import nullifyEmptyStrings from '../../../../lib/utils/nullifyEmptyStrings';

export default class ContractTemplateSignatureBlocks extends React.Component {
  static propTypes = {
    canEdit: PropTypes.bool.isRequired,
    relay: PropTypes.object.isRequired,
    node: PropTypes.object.isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      contract: {},
      localSignatureBlocks: _.cloneDeep(
        this.props.node.c_template_signature_blocks.edges
      ),
      editingNewSignatureBlock: false
    };
    this.reset = this.reset.bind(this);
    this.deleteBlock = this.deleteBlock.bind(this);
    this.update = this.update.bind(this);
    this.onChange = this.onChange.bind(this);

    this.newSignatureBlockChangeHandler = this.newSignatureBlockChangeHandler.bind(
      this
    );
    this.closeNewSignatureBlockEditor = this.closeNewSignatureBlockEditor.bind(
      this
    );
    this.addSignatureBlock = this.addSignatureBlock.bind(this);
    this.resetAddSignatureBlock = this.resetAddSignatureBlock.bind(this);
    this.createSignatureBlock = this.createSignatureBlock.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    // Update localSignatureBlocks if any edges are added/deleted
    if (
      this.state.localSignatureBlocks.length !==
      nextProps.node.c_template_signature_blocks.edges.length
    ) {
      const obj = _.cloneDeep(this.state);
      obj.localSignatureBlocks = _.cloneDeep(
        nextProps.node.c_template_signature_blocks.edges
      );
      this.setState({ ...obj });
    }
  }

  onChange(signatureBlock, idx) {
    this.setState(state => {
      const obj = _.cloneDeep(state);
      obj.localSignatureBlocks[idx].node = signatureBlock;
      return obj;
    });
  }

  reset(idx) {
    const obj = _.cloneDeep(this.state);
    obj.localSignatureBlocks[idx] = _.cloneDeep(
      this.props.node.c_template_signature_blocks.edges[idx]
    );
    this.setState(obj);
  }

  addSignatureBlock() {
    const obj = _.cloneDeep(this.state);
    obj.editingNewSignatureBlock = true;
    if (!this.state.contract.newSignatureBlock) {
      obj.contract.newSignatureBlock = {
        node: {
          address_visibility: 'HIDE',
          company_name_visibility: 'HIDE',
          email_visibility: 'SHOW',
          name_visibility: 'SHOW',
          phone_visibility: 'SHOW',
          signing_party: '',
          title_visibility: 'HIDE'
        }
      };
    }
    this.setState({ ...obj });
  }

  newSignatureBlockChangeHandler(signatureBlock) {
    const obj = _.cloneDeep(this.state);
    obj.contract.newSignatureBlock.node = signatureBlock;
    this.setState({ ...obj });
  }

  closeNewSignatureBlockEditor() {
    this.setState({ editingNewSignatureBlock: false });
  }

  resetAddSignatureBlock() {
    this.setState(state => {
      const obj = _.cloneDeep(state);
      delete obj.contract.newSignatureBlock;
      return obj;
    });
  }

  createSignatureBlock(index, successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to add signature block',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
      const obj = _.cloneDeep(this.state);
      delete obj.contract.newSignatureBlock;
      this.setState({ ...obj });
      GlobalNotifications.show({
        message: 'Successfully added signature block',
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('created-signature-block');
    };

    const mutation = graphql`
      mutation SignatureBlocks_ContractTemplate_createCTemplateSignatureBlockMutation(
        $input: CreateCTemplateSignatureBlockInput!
      ) {
        createCTemplateSignatureBlock(input: $input) {
          new_c_template_signature_block_edge {
            node {
              id
              address_visibility
              company_name_visibility
              email_visibility
              name_visibility
              phone_visibility
              signing_party
              title_visibility
            }
          }
        }
      }
    `;

    const signatureBlock = _.pick(nullifyEmptyStrings(
      this.state.contract.newSignatureBlock.node
    ), [
      'address_visibility',
      'company_name_visibility',
      'email_visibility',
      'name_visibility',
      'phone_visibility',
      'signing_party',
      'title_visibility'
    ]);

    const parentID = this.props.node.id;

    const configs = [
      {
        type: 'RANGE_ADD',
        parentID,
        edgeName: 'new_c_template_signature_block_edge',
        connectionInfo: [
          {
            key: 'ContractTemplateContainer_c_template_signature_blocks',
            rangeBehavior: 'append'
          }
        ]
      }
    ];

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          c_template_id: parentID,
          ...signatureBlock
        }
      },
      configs,
      onCompleted,
      onError
    });
  }

  update(idx, successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to update signature block',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
      GlobalNotifications.show({
        message: 'Successfully updated signature block',
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('updated-signature-block');
    };

    const signatureBlock = _.pick(
      nullifyEmptyStrings(this.state.localSignatureBlocks[idx].node),
      [
        'id',
        'address_visibility',
        'company_name_visibility',
        'email_visibility',
        'name_visibility',
        'phone_visibility',
        'signing_party',
        'title_visibility'
      ]
    );

    const mutation = graphql`
      mutation SignatureBlocks_ContractTemplate_updateCTemplateSignatureBlockMutation(
        $input: UpdateCTemplateSignatureBlockInput!
      ) {
        updateCTemplateSignatureBlock(input: $input) {
          c_template_signature_block {
            id
            address_visibility
            company_name_visibility
            email_visibility
            name_visibility
            phone_visibility
            signing_party
            title_visibility
          }
        }
      }
    `;

    const optimisticResponse = {
      updateCTemplateSignatureBlock: {
        c_template_signature_block: _.cloneDeep(signatureBlock)
      }
    };
    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          ...signatureBlock
        }
      },
      optimisticResponse,
      onCompleted,
      onError
    });
  }

  deleteBlock(idx, successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to delete signature block',
        intent: Intent.DANGER
      });
    };

    const onCompleted = () => {
      if (successCb) successCb();
      GlobalNotifications.show({
        message: 'Successfully deleted signature block',
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('deleted-signature-block');
    };

    const mutation = graphql`
      mutation SignatureBlocks_ContractTemplate_deleteCTemplateSignatureBlockMutation(
        $input: DeleteCTemplateSignatureBlockInput!
      ) {
        deleteCTemplateSignatureBlock(input: $input) {
          deletedCTemplateSignatureBlockId
        }
      }
    `;

    const environment = this.props.relay.environment;
    const parentID = this.props.node.id;

    const configs = [
      {
        type: 'RANGE_DELETE',
        parentID,
        pathToConnection: ['c_template', 'c_template_signature_blocks'],
        deletedIDFieldName: 'deletedCTemplateSignatureBlockId',
        connectionKeys: [
          {
            key: 'ContractTemplateContainer_c_template_signature_blocks',
            rangeBehavior: 'append'
          }
        ]
      }
    ];

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id: this.props.node.c_template_signature_blocks.edges[idx].node.id
        }
      },
      configs,
      onCompleted,
      onError
    });
  }

  render() {
    return (
      <SignatureBlocks
        signatureBlocks={this.props.node.c_template_signature_blocks.edges}
        localSignatureBlocks={this.state.localSignatureBlocks}
        onChange={this.onChange}
        reset={this.reset}
        deleteBlock={this.deleteBlock}
        update={this.update}
        readOnly={!this.props.canEdit}
        resetAddSignatureBlock={this.resetAddSignatureBlock}
        newSignatureBlockChangeHandler={this.newSignatureBlockChangeHandler}
        closeNewSignatureBlockEditor={this.closeNewSignatureBlockEditor}
        addSignatureBlock={this.addSignatureBlock}
        newSignatureBlock={this.state.contract.newSignatureBlock}
        editingNewSignatureBlock={this.state.editingNewSignatureBlock}
        createSignatureBlock={this.createSignatureBlock}
        isTemplate
      />
    );
  }
}



// WEBPACK FOOTER //
// ./webpack/components/Document/Template/Contract/SignatureBlocks.js